import React, { FormEvent } from 'react'
import { useForm, Link, router } from '@inertiajs/react'
import * as yup from 'yup'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import Divider from '@mui/material/Divider'
import { AppLayout } from '@/components/layouts/AppLayout'
import { ColorPicker } from '@/components/shared/ColorPicker'

interface Props {
  board: {
    id: number
    name: string
    slug: string
    color: string | null
  }
}

const boardSchema = yup.object({
  board: yup.object({
    name: yup.string().required('Board name is required').max(100, 'Board name must be at most 100 characters'),
  })
})

export default function Edit({ board }: Props) {
  const { data, setData, put, processing, errors, setError, clearErrors } = useForm({
    board: {
      name: board.name,
      color: board.color || '',
    },
  })

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    clearErrors()

    try {
      boardSchema.validateSync(data, { abortEarly: false })
    } catch (err) {
      if (err instanceof yup.ValidationError) {
        const validationErrors: Record<string, string> = {}
        err.inner.forEach((e) => {
          if (e.path) validationErrors[e.path] = e.message
        })
        setError(validationErrors)
        return
      }
    }

    put(`/tasks/boards/${board.slug}`)
  }

  function handleDelete() {
    if (confirm(`Are you sure you want to delete board '${board.name}'? This will delete all tasks and statuses. This cannot be undone.`)) {
      router.delete(`/tasks/boards/${board.slug}`)
    }
  }

  return (
    <AppLayout title={`Edit board: ${board.name}`}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, sm: 5 },
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          maxWidth: 600,
        }}
      >
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <TextField
            label="Board name"
            fullWidth
            autoFocus
            value={data.board.name}
            onChange={e => setData('board', { ...data.board, name: e.target.value })}
            error={'board.name' in errors}
            helperText={(errors as Record<string, string>)['board.name']}
            sx={{ mb: 2.5 }}
          />
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Color</Typography>
            <ColorPicker
              value={data.board.color}
              onChange={(color) => setData('board', { ...data.board, color })}
            />
          </Box>
          <Button
            type="submit"
            variant="contained"
            fullWidth
            size="large"
            disabled={processing}
            sx={{ mb: 2 }}
          >
            Update board
          </Button>
          <Box sx={{ textAlign: 'center' }}>
            <Link href={`/tasks/boards/${board.slug}/settings`} style={{ color: '#1a1a1a' }}>Back to settings</Link>
          </Box>
        </Box>
        <Divider sx={{ my: 3 }} />
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Danger zone
          </Typography>
          <Button
            variant="outlined"
            color="error"
            fullWidth
            onClick={handleDelete}
          >
            Delete this board
          </Button>
        </Box>
      </Paper>
    </AppLayout>
  )
}
